import React, { useEffect, useState } from 'react'
import { fetchReviewSummary, fetchReviews, Review, ReviewSummary } from '@/services/reviewsApi'
import { capDisplayRating } from '@/utils/ratingDisplay'
import ReviewStars from './reviews/ReviewStars'
import { getColorwaySwatch, getInitial } from './reviews/reviewColorways'
import { ChevronRight } from 'lucide-react'
import ProductReviewsModal from './reviews/ProductReviewsModal'

interface InlineReviewsProps {
    productId: string
    region?: string
    className?: string
}

const InlineReviews: React.FC<InlineReviewsProps> = ({ productId, region, className = '' }) => {
    const [summary, setSummary] = useState<ReviewSummary | null>(null)
    const [snippets, setSnippets] = useState<Review[]>([])
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)

    useEffect(() => {
        let active = true
        setLoading(true)
        Promise.all([
            fetchReviewSummary(productId, region),
            fetchReviews(productId, { sort: 'most_helpful', limit: 2 }, region),
        ])
            .then(([summaryData, listData]) => {
                if (!active) return
                setSummary(summaryData)
                setSnippets(listData.items)
            })
            .catch(() => { })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => { active = false }
    }, [productId, region])

    if (loading) {
        return (
            <div className={`animate-pulse ${className}`}>
                <div className="rounded-[20px] bg-white border border-[#E9E9E9] p-5">
                    <div className="h-4 w-32 rounded bg-[#EEE] mb-3" />
                    <div className="h-3 w-48 rounded bg-[#EEE]" />
                </div>
            </div>
        )
    }

    if (!summary || summary.total === 0) return null

    const average = capDisplayRating(summary.average ?? 0)

    return (
        <div className={className}>
            <div className="w-full text-left rounded-[20px] bg-white border border-[#E9E9E9] p-5 transition-all duration-200 hover:shadow-[0_2px_12px_rgba(0,0,0,0.08)] hover:border-orange/20">
                <div className="flex items-center justify-between mb-3">
                    <span className="font-headline text-xs uppercase tracking-[0.08em] text-orange">
                        Reviews
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowModal(true)}
                        className="flex items-center gap-1 text-[13px] text-[#8A8A8A] font-body transition-colors duration-200 hover:text-orange"
                    >
                        Read all
                        <ChevronRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </button>
                </div>

                <div className="flex items-center gap-3 mb-3">
                    <span className="font-headline text-[28px] leading-none text-black">{average}</span>
                    <div className="flex flex-col gap-0.5">
                        <ReviewStars rating={average} size={14} />
                        <span className="text-[12px] text-[#8A8A8A]">Based on {(summary.total ?? 0).toLocaleString()} reviews</span>
                    </div>
                </div>

                {typeof summary.wouldBuildAgainPct === 'number' && (
                    <div className="text-[13px] leading-[1.4] text-green font-medium mb-3">
                        {summary.wouldBuildAgainPct}% would build a second pair.
                    </div>
                )}

                {snippets.length > 0 && (
                    <div className="space-y-2.5 pt-3 border-t border-[#E9E9E9]">
                        {snippets.map((review) => {
                            const swatch = getColorwaySwatch(review.colorway, review.id)
                            return (
                                <div key={review.id} className="flex items-start gap-2.5">
                                    <div
                                        className="w-7 h-7 rounded-full flex items-center justify-center font-headline text-[11px] flex-none mt-0.5"
                                        style={{ background: swatch.bg, color: swatch.text }}
                                    >
                                        {getInitial(review.authorName)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            <ReviewStars rating={review.rating ?? 0} size={10} />
                                            <span className="text-[11px] text-[#8A8A8A]">{review.authorName}</span>
                                        </div>
                                        {(review.body || review.title) && <p className="text-[13px] leading-[1.4] text-[#3D3D3D] line-clamp-2">
                                            &ldquo;{review.body || review.title}&rdquo;
                                        </p>}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    className="mt-3 w-full py-2.5 rounded-full bg-white border border-[#E9E9E9] font-headline text-[13px] text-[#6B6B6B] transition-all duration-200 hover:border-orange hover:text-orange hover:bg-orange/5"
                >
                    Read more reviews
                </button>
            </div>

            {showModal && (
                <ProductReviewsModal
                    productId={productId}
                    region={region}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    )
}

export default InlineReviews
